import { useAuth } from "../auth.js";
import { AdminBreadcrumb } from "./AdminHome.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { requiredIdentifiers } from "@titleverify/shared";
import { apiErrorText, apiFetch, type ProfessionalProfile } from "../api.js";

/**
 * The admin verification queue (MD1).
 *
 * This is an ops surface, not a directory. It shows named professionals to
 * admins so they can check enrolments — which is the opposite of advertising
 * them to attract work (COMPLIANCE Constraint 1). Nothing here is reachable by a
 * client, and the server refuses `professional_profile:list` to every role but
 * admin regardless of what this page renders.
 */

/**
 * THE TWO IDENTIFIERS, AND WHY A BARE DASH WAS WRONG HERE.
 * ===========================================================================
 *
 * Attorney-at-law and notary public are separate qualifications under separate
 * instruments — enrolled by the Supreme Court, commissioned under the Notaries
 * Ordinance. So a blank enrolment number means one of two OPPOSITE things:
 *
 *   - Not applicable. They claim to be a notary, and a notary has no Supreme
 *     Court enrolment number. Nothing is missing; there was never anything to
 *     give.
 *   - Missing. They claim to be an attorney, and the one identifier their claim
 *     rests on is not there. There is nothing for the admin to check.
 *
 * Rendering both as "—" on the screen that gates non-negotiable 4 asks the admin
 * to guess which, one line above the Verify button. Which of the two applies is
 * decided by the claimed `type` via `requiredIdentifiers` — the same shared rule
 * the server checks at submit and the `verified_needs_identifier` constraint
 * (migration 0002) enforces at `verified` — not by a copy of it written here.
 *
 * The missing case should be unreachable: the server refuses the move to
 * `pending` when a required identifier is absent. It is stated loudly rather
 * than assumed away, because if it ever does appear the Verify button will fail
 * against a database CHECK constraint, and the admin should learn that from this
 * panel rather than from a constraint error.
 */
function IdentifierRows({ profile }: { profile: ProfessionalProfile }) {
  const required = requiredIdentifiers(profile.type);

  return (
    <>
      {IDENTIFIERS.map((field) => {
        const value = profile[field];
        const isRequired = required.includes(field);

        return (
          <Fragment key={field}>
            <dt>{IDENTIFIER_LABEL[field]}</dt>
            <dd>
              {value !== null ? (
                <>
                  {value}
                  {/*
                    Stored but not part of what they claim. Worth showing — it is
                    real data an admin may want — but not worth letting it read
                    as a credential their claim rests on.
                  */}
                  {isRequired ? null : (
                    <>
                      {" "}
                      <span className="muted">
                        — on file, but not required by what they claim to be
                      </span>
                    </>
                  )}
                </>
              ) : isRequired ? (
                <strong>{IDENTIFIER_MISSING[field]}</strong>
              ) : (
                <span className="muted">{IDENTIFIER_NOT_APPLICABLE[field]}</span>
              )}
            </dd>
          </Fragment>
        );
      })}
    </>
  );
}

const IDENTIFIERS = ["enrolmentNo", "notaryRegistrationNo"] as const;

type Identifier = (typeof IDENTIFIERS)[number];

const IDENTIFIER_LABEL: Record<Identifier, string> = {
  enrolmentNo: "Enrolment no. (Supreme Court)",
  notaryRegistrationNo: "Notary registration no.",
};

/* Absent when the claim depends on it. A gap in the evidence, and said as one. */
const IDENTIFIER_MISSING: Record<Identifier, string> = {
  enrolmentNo:
    "Not given — and their claim rests on it. There is no enrolment number to check against the roll.",
  notaryRegistrationNo:
    "Not given — and their claim rests on it. There is no registration number to check against the Notaries register.",
};

/* Absent because the claim never called for it. Nothing is missing. */
const IDENTIFIER_NOT_APPLICABLE: Record<Identifier, string> = {
  enrolmentNo: "Not applicable — they claim to be a notary, not an attorney-at-law.",
  notaryRegistrationNo: "Not applicable — they claim to be an attorney-at-law, not a notary.",
};

const REJECTION_REASONS = [
  "ENROLMENT_NOT_FOUND",
  "DETAILS_MISMATCH",
  "EVIDENCE_INSUFFICIENT",
  "DUPLICATE_APPLICATION",
  "OTHER",
] as const;

export function AdminProfessionals() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<Record<string, string>>({});
  /*
   * WHICH REJECTION IS ARMED, keyed `${profileId}:${reason}`.
   *
   * Five reject buttons sat in a row and fired on a single click, on the screen
   * that decides whether an attorney may receive work at all. A slip does not
   * just refuse this person — it removes them from this queue, and there is no
   * way back: `rejected -> pending` IS a legal transition in the state machine,
   * but the decision endpoint accepts only verify / reject / revoke, so nothing
   * in the product can perform it. Undoing one today means a database write.
   *
   * So the second click is not ceremony. It is the only thing standing between a
   * mis-aimed cursor and a state the operator cannot get out of.
   */
  const [arming, setArming] = useState<string | null>(null);

  const { data, error, isPending } = useQuery({
    queryKey: ["admin", "professionals", "pending"],
    queryFn: async () =>
      apiFetch<{ professionals: ProfessionalProfile[] }>("/admin/professionals?status=pending", {
        token: await getToken(),
      }),
  });

  const decide = useMutation({
    mutationFn: async (input: { id: string; body: Record<string, unknown> }) =>
      apiFetch<ProfessionalProfile>(`/admin/professionals/${input.id}/decision`, {
        token: await getToken(),
        method: "POST",
        body: input.body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "professionals"] }),
  });

  if (isPending) return <p className="muted" role="status">Loading the review queue…</p>;
  if (error) return <p role="alert">{apiErrorText(error, "Could not load the review queue.")}</p>;

  const pending = data.professionals;

  // An honest empty state. Nothing is pending; that is a fact, not a failure.
  if (pending.length === 0) {
    return (
      <section className="wide">
        <AdminBreadcrumb />
        <header className="page-head">
          <p className="eyebrow">Enrolment checks</p>
          <h2>Verification queue</h2>
        </header>
        <p className="empty">No professionals are awaiting verification.</p>
      </section>
    );
  }

  return (
    <section className="wide">
      <AdminBreadcrumb />
      <header className="page-head">
        <p className="eyebrow">Enrolment checks</p>
        <h2>Verification queue</h2>
        <p className="lede">
          {pending.length} awaiting review. A professional is not assignable to any case until
          their enrolment is verified here.
        </p>
      </header>

      {decide.error ? <p role="alert">{apiErrorText(decide.error, "Could not record that decision.")}</p> : null}

      {/* Forms, not a table: the queue keeps a form-sized measure inside the wide page. */}
      <ul className="queue">
        {pending.map((profile) => (
          <li key={profile.id} className="card">
            {/*
              The standing, as the server sent it. It is always `pending` on this
              queue, and rendering it anyway means the word an admin acts on is
              the word the server holds rather than one this screen assumed.
            */}
            <p>
              <span className={`status status-${profile.verificationStatus}`}>
                {profile.verificationStatus}
              </span>
            </p>

            <dl>
              <dt>Claims to be</dt>
              <dd>{profile.type}</dd>
              <IdentifierRows profile={profile} />
            </dl>

            {/*
              Required, and it is the whole point of the screen: the admin must
              record what they actually checked, not merely that they approved.
              The server refuses an empty source and the database refuses a
              verified row without one (COMPLIANCE Constraint 5).
            */}
            <div className="field">
              <label htmlFor={`source-${profile.id}`}>
                <strong>What did you check, and how?</strong>
              </label>
              <input
                id={`source-${profile.id}`}
                value={source[profile.id] ?? ""}
                onChange={(e) => setSource((s) => ({ ...s, [profile.id]: e.target.value }))}
                placeholder="e.g. SC roll inspected at the Registry, 16 July 2026"
              />
            </div>

            <div className="actions">
              <button
                type="button"
                disabled={!source[profile.id]?.trim() || decide.isPending}
                onClick={() =>
                  decide.mutate({
                    id: profile.id,
                    body: { decision: "verify", verificationSource: source[profile.id] },
                  })
                }
              >
                Verify
              </button>
            </div>

            <div className="actions">
              {REJECTION_REASONS.map((reason) => {
                const key = `${profile.id}:${reason}`;
                const armed = arming === key;
                return (
                  <button
                    key={reason}
                    type="button"
                    className={armed ? "compact" : "quiet compact"}
                    disabled={decide.isPending}
                    // The first click arms; the second commits. Arming is
                    // per-button, so moving to a different reason disarms the
                    // previous one rather than leaving two live at once.
                    onClick={() => {
                      if (!armed) {
                        setArming(key);
                        return;
                      }
                      setArming(null);
                      decide.mutate({ id: profile.id, body: { decision: "reject", reason } });
                    }}
                  >
                    {armed
                      ? `Confirm: reject — ${reason.toLowerCase().replaceAll("_", " ")}`
                      : `Reject: ${reason.toLowerCase().replaceAll("_", " ")}`}
                  </button>
                );
              })}
              {arming?.startsWith(`${profile.id}:`) ? (
                <p className="muted">
                  <small>
                    Rejecting takes this person out of the queue, and nothing in this
                    application puts them back — the transition is allowed but no screen or
                    endpoint performs it. Choose the button again to go ahead, or{" "}
                    <button type="button" className="linkish" onClick={() => setArming(null)}>
                      leave it
                    </button>
                    .
                  </small>
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
